import AnalyticsService from '@root/core/src/services/analytics-service';
import Button from '@root/core/src/components/button';
import CaptchaService from '@root/core/src/services/captcha-service';
import InlineError from '@root/core/src/components/inline-error';
import Input, { TextTypes } from '@root/core/src/components/input';
import PropTypes from '@root/vendor/prop-types';
import React, { useEffect, useState } from '@root/vendor/react';
import submitLeadCaptureToBraze from '@root/core/src/api/submit-lead-capture-to-braze';
import useForm from '@root/core/src/hooks/use-form';
import useImperativeNetworkRequest from '@root/core/src/hooks/use-imperative-network-request';
import { H2, P1 } from '@root/gatsby-contentful-core/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';
import { isRequired, isValidEmail } from '@root/core/src/utils/validators';
import { useRootId } from '@root/attribution/src/hooks/use-root-id';

const SCENES = {
  REQUEST_EMAIL: 'request-email',
  SUBMISSION_SUCCESS: 'submission-success',
  SUBMISSION_FAILED: 'submission-failed',
};

const validations = [
  isRequired('email'),
  isValidEmail('email'),
];

export default function GoingPlacesFormFlow({
  onClose, trackClick, trackEvent,
}) {
  const [currentScene, setCurrentScene] = useState(SCENES.REQUEST_EMAIL);
  const [errorMessage, setErrorMessage] = useState(null);
  const [postLeadCaptureToBraze] = useImperativeNetworkRequest(submitLeadCaptureToBraze);
  const form = useForm({
    validations,
    validateOnBlur: false,
  });
  const rootId = useRootId();

  useEffect(() => {
    CaptchaService.setup();
  }, []);

  useEffect(() => {
    trackEvent('VIEWED_GOING_PLACES_MODAL');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit({ email }) {
    trackClick('GOING_PLACES_EMAIL_FORM_SUBMIT');

    try {
      const captchaToken = await CaptchaService.challenge('Submit_Lead_Capture_To_Braze');

      const result = await postLeadCaptureToBraze({
        captchaToken,
        email,
        lastTouchAttribution: 'GOING_PLACES_PHOENIX',
        mixpanelDistinctId: AnalyticsService.getDistinctId(),
        interested_in_auto: true,
        market: 'AZ',
        rootId,
      });

      if (result.isSuccess()) {
        setCurrentScene(SCENES.SUBMISSION_SUCCESS);
      } else {
        setErrorMessage(true);
      }
    } catch {
      setErrorMessage(true);
    }
  }

  if (currentScene === SCENES.SUBMISSION_SUCCESS) {
    return (
      <div>
        <H2 css={styles.headline}>You’re on the list.</H2>
        <P1>Keep an eye on your inbox for an upcoming offer exclusive to Phoenix-area drivers.</P1>
        <Button
          css={styles.spaceAboveForm}
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <div>
      <H2 css={styles.headline}>Something&apos;s coming your way, Phoenix.</H2>
      <P1>Enter your email and we&apos;ll contact you as soon as we launch with an exciting offer for Phoenix-area drivers.</P1>
      <form
        css={[styles.form, styles.spaceAboveForm]}
        onSubmit={form.createSubmitHandler(handleSubmit)}
      >
        <Input
          autoFocus={true}
          id={'email'}
          inputType={TextTypes.EMAIL}
          label={'Email address'}
          {...form.getFieldProps('email')}
        />
        {errorMessage ?
          <InlineError message={'Something went wrong. Please try again later.'} /> :
          <Button
            className={'button'}
            disabled={form.submitted || form.submitting}
          >
            Submit
          </Button>
        }
      </form>
    </div>
  );
}

GoingPlacesFormFlow.propTypes = {
  onClose: PropTypes.func.isRequired,
  trackClick: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  headline: {
    marginRight: 20,
    marginBottom: 16,
  },
  spaceAboveForm: {
    marginTop: 34,
  },
  form: {
    button: {
      marginTop: 14,
    },
  },
});
